import {
  cloudflareAiAccessTokenPrefix,
  cloudflareAiConnectorName,
  cloudflareAiInstallTokenSecretId,
} from "@p0security/integrations-cloudflare-ai-shared/secret-manager";
import type { CloudflareAiComponents } from "@p0security/integrations-cloudflare-ai-shared/types";
import type { FrontendInstallContext } from "@p0security/integrations-installer";
import {
  awsConnectorTf,
  gcpConnectorTf,
  p0InvokerEmail,
  p0InvokerRoleName,
  secretProviderFor,
  shellLines,
  tfId,
} from "@p0security/integrations-vaulted-credential-shared";
import type { ItemConfigOf } from "@p0security/shared/install/types";

type SecretManagerConfig = ItemConfigOf<
  CloudflareAiComponents["credential"]
>["secretManager"];

export type InstallContext = FrontendInstallContext<unknown>;

const GCP_CONNECTOR_IMAGE = {
  imageName: "docker.io/p0security/p0-connector-cloudflare-ai-gcloud",
  digest:
    "sha-ae47c26@sha256:3d09fdd0383b9b9db62b6d9786ab17226382d328b7d7182d61628089ee2132e5",
} as const;

const AWS_CONNECTOR_IMAGE = {
  imageName: "p0security/p0-connector-cloudflare-ai-aws",
  digest:
    "sha-ae47c26@sha256:cc5b6822299329f70ed09e8032a9419e11a8d6cce0708e154b90889c109f24b1",
} as const;

/** The environment variable that the store command reads the install token
 * from, so the token stays out of the shell history. */
export const INSTALL_TOKEN_ENV = "CLOUDFLARE_INSTALL_TOKEN";

const NAMES = {
  resource: "cloudflare",
  role: "Cloudflare",
  label: "Cloudflare",
} as const;

const awsInstallTokenSecretId = (itemId: string) =>
  secretProviderFor("aws").replacer(cloudflareAiInstallTokenSecretId(itemId));

export const awsStoreInstallToken = (
  itemId: string,
  secretManager: Extract<SecretManagerConfig, { type: "aws-sm" }>
) =>
  shellLines(
    `printf '%s' "$${INSTALL_TOKEN_ENV}"`,
    "| aws secretsmanager put-secret-value",
    `--secret-id ${awsInstallTokenSecretId(itemId)}`,
    `--region ${secretManager.secretsRegion}`,
    "--secret-string file:///dev/stdin"
  );

export const awsTf = (
  context: InstallContext,
  itemId: string,
  secretManager: Extract<SecretManagerConfig, { type: "aws-sm" }>
) =>
  awsConnectorTf({
    names: NAMES,
    image: AWS_CONNECTOR_IMAGE,
    accountId: secretManager.install,
    region: secretManager.connectorRegion,
    secretsRegion: secretManager.secretsRegion,
    connectorName: cloudflareAiConnectorName,
    p0RoleName: p0InvokerRoleName(context),
    tokenSecretPrefix: secretProviderFor("aws").replacer(
      cloudflareAiAccessTokenPrefix(itemId)
    ),
    installSecret: {
      resource: "install_token",
      itemTfId: tfId(itemId),
      secretId: awsInstallTokenSecretId(itemId),
      label: "install token",
    },
  });

const gcpInstallTokenSecretId = (itemId: string) =>
  secretProviderFor("gcp").replacer(cloudflareAiInstallTokenSecretId(itemId));

export const gcpStoreInstallToken = (
  itemId: string,
  secretManager: Extract<SecretManagerConfig, { type: "gcp-sm" }>
) =>
  shellLines(
    `printf '%s' "$${INSTALL_TOKEN_ENV}"`,
    `| gcloud secrets versions add ${gcpInstallTokenSecretId(itemId)}`,
    `--project=${secretManager.install}`,
    "--data-file=-"
  );

export const gcpTf = (
  context: InstallContext,
  itemId: string,
  secretManager: Extract<SecretManagerConfig, { type: "gcp-sm" }>
) =>
  gcpConnectorTf({
    names: NAMES,
    image: GCP_CONNECTOR_IMAGE,
    projectId: secretManager.install,
    region: secretManager.connectorRegion,
    connectorName: secretManager.connectorServiceName,
    serviceAccountId:
      secretManager.connectorServiceAccount?.split("@")[0] ?? "",
    invokerEmail: p0InvokerEmail(context.installed),
    tokenSecretPrefix: secretProviderFor("gcp").replacer(
      cloudflareAiAccessTokenPrefix(itemId)
    ),
    installSecret: {
      resource: "install_token",
      itemTfId: tfId(itemId),
      secretId: gcpInstallTokenSecretId(itemId),
      label: "install token",
    },
  });
